import process from "node:process";
import readline from "node:readline";
import { spawn } from "node:child_process";
import {
  saveAuth,
  setDefaultProfile,
  envLabelForApiUrl,
  orgProfileKey,
  ENV_URLS,
  envToProfile,
  getArgValue,
  type StoredAuth,
} from "./auth";
import { readBrandEnv } from "./brand.js";
import { DEFAULT_API_URL, DEFAULT_APP_URL } from "./default-urls.js";

export type StartResponse = {
  ok: boolean;
  deviceCode?: string;
  userCode?: string;
  verificationUrl?: string;
  expiresAt?: string;
  intervalSeconds?: number;
  error?: string;
};

export type PollResponse = {
  ok: boolean;
  status?: "pending" | "approved" | "rejected" | "expired";
  accessToken?: string;
  orgId?: string;
  error?: string;
};

export type OrgsResponse = {
  ok: boolean;
  currentOrgId?: string;
  organizations?: Array<{ id: string; name: string; role: string }>;
  error?: string;
};

export type SwitchOrgResponse = {
  ok: boolean;
  accessToken?: string;
  orgId?: string;
  orgName?: string;
  role?: string;
  error?: string;
};

function shouldOpenBrowser(argv: string[]): boolean {
  return !argv.includes("--no-open");
}

export function openUrl(url: string) {
  const platform = process.platform;
  if (platform === "darwin") {
    spawn("open", [url], { stdio: "ignore" });
    return;
  }
  if (platform === "win32") {
    spawn("cmd", ["/c", "start", "", url], { stdio: "ignore" });
    return;
  }
  spawn("xdg-open", [url], { stdio: "ignore" });
}

function promptChoice(question: string): Promise<string> {
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

export async function fetchOrgs(apiUrl: string, token: string): Promise<OrgsResponse | null> {
  try {
    const res = await fetch(`${apiUrl}/cli-login/orgs`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as OrgsResponse;
  } catch {
    return null;
  }
}

export async function switchOrg(apiUrl: string, token: string, orgId: string): Promise<SwitchOrgResponse> {
  const res = await fetch(`${apiUrl}/cli-login/switch-org`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ orgId }),
  });
  return (await res.json()) as SwitchOrgResponse;
}

export async function runLogin(argv: string[]) {
  const env = getArgValue(argv, "--env");
  const envUrls = env ? ENV_URLS[env] : undefined;

  if (env && !envUrls) {
    console.error(`Unknown environment: ${env}`);
    console.error("Valid environments: prod, dev, local");
    process.exit(1);
  }

  const apiUrl =
    getArgValue(argv, "--api-url") ??
    envUrls?.api ??
    readBrandEnv("CANARY_API_URL") ??
    DEFAULT_API_URL;
  const appUrl =
    getArgValue(argv, "--app-url") ??
    envUrls?.app ??
    process.env.CANARY_APP_URL ??
    DEFAULT_APP_URL;

  const orgFlag = getArgValue(argv, "--org");

  const startRes = await fetch(`${apiUrl}/cli-login/start`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ appUrl }),
  });

  const startJson = (await startRes.json()) as StartResponse;
  if (!startRes.ok || !startJson.ok || !startJson.deviceCode || !startJson.userCode) {
    console.error("Login start failed", startJson.error ?? startRes.statusText);
    process.exit(1);
  }

  console.log("Login required.");
  console.log(`User code: ${startJson.userCode}`);
  if (startJson.verificationUrl) {
    console.log(`Open: ${startJson.verificationUrl}`);
    if (shouldOpenBrowser(argv)) {
      try {
        openUrl(startJson.verificationUrl);
      } catch {
        console.log("Unable to open browser automatically. Please open the URL manually.");
      }
    }
  }

  const intervalMs = (startJson.intervalSeconds ?? 3) * 1000;
  const expiresAt = startJson.expiresAt ? new Date(startJson.expiresAt).getTime() : null;

  let token: string | undefined;
  let initialOrgId: string | undefined;

  while (true) {
    if (expiresAt && Date.now() > expiresAt) {
      console.error("Login code expired.");
      process.exit(1);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));

    const pollRes = await fetch(`${apiUrl}/cli-login/poll`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ deviceCode: startJson.deviceCode }),
    });

    const pollJson = (await pollRes.json()) as PollResponse;
    if (!pollRes.ok || !pollJson.ok) {
      console.error("Login poll failed", pollJson.error ?? pollRes.statusText);
      process.exit(1);
    }

    if (pollJson.status === "approved" && pollJson.accessToken) {
      token = pollJson.accessToken;
      initialOrgId = pollJson.orgId;
      break;
    }

    if (pollJson.status === "rejected") {
      console.error("Login rejected.");
      process.exit(1);
    }

    if (pollJson.status === "expired") {
      console.error("Login expired.");
      process.exit(1);
    }
  }

  // Fetch user's organizations (gracefully skip if endpoint not available)
  const orgsData = await fetchOrgs(apiUrl, token);
  const orgs = orgsData?.organizations ?? [];

  let finalToken = token;
  let finalOrgId = initialOrgId;
  let finalOrgName: string | undefined;

  if (orgs.length <= 1) {
    // Single org (or none) — use as-is
    finalOrgName = orgs[0]?.name;
  } else {
    // Multiple orgs — determine which one to use
    let selectedOrg: (typeof orgs)[number] | undefined;

    if (orgFlag) {
      // Match --org flag by name (case-insensitive) or ID
      selectedOrg = orgs.find(
        (o) => o.name.toLowerCase() === orgFlag.toLowerCase() || o.id === orgFlag
      );
      if (!selectedOrg) {
        console.error(`Organization "${orgFlag}" not found. Available orgs:`);
        for (const o of orgs) {
          console.error(`  - ${o.name} (${o.id})`);
        }
        process.exit(1);
      }
    } else if (process.stdin.isTTY) {
      // Interactive prompt
      console.log("\nYou belong to multiple organizations. Select one:");
      for (let i = 0; i < orgs.length; i++) {
        const marker = orgs[i].id === initialOrgId ? " (current)" : "";
        console.log(`  ${i + 1}. ${orgs[i].name}${marker}`);
      }

      const answer = await promptChoice(`\nChoice [1-${orgs.length}]: `);
      const idx = parseInt(answer, 10) - 1;
      if (isNaN(idx) || idx < 0 || idx >= orgs.length) {
        console.error("Invalid selection.");
        process.exit(1);
      }
      selectedOrg = orgs[idx];
    } else {
      // Non-interactive — use default from JWT
      const defaultOrg = orgs.find((o) => o.id === initialOrgId);
      console.log(
        `Warning: Multiple organizations available but running non-interactively. Using "${defaultOrg?.name ?? initialOrgId}".`
      );
      console.log('Tip: Use --org <name> to select a specific organization.');
      selectedOrg = defaultOrg;
    }

    if (selectedOrg && selectedOrg.id !== initialOrgId) {
      // Switch to selected org
      const switchRes = await switchOrg(apiUrl, token, selectedOrg.id);
      if (!switchRes.ok || !switchRes.accessToken) {
        console.error("Failed to switch organization:", switchRes.error ?? "Unknown error");
        process.exit(1);
      }
      finalToken = switchRes.accessToken;
      finalOrgId = switchRes.orgId;
      finalOrgName = switchRes.orgName;
    } else if (selectedOrg) {
      finalOrgName = selectedOrg.name;
    }
  }

  const envLabel = env ? envToProfile(env) : envLabelForApiUrl(apiUrl);
  const sessionAuth: StoredAuth = {
    token: finalToken,
    apiUrl,
    orgId: finalOrgId,
    orgName: finalOrgName,
    envLabel,
  };

  // Store the session under an org + env keyed profile so multiple orgs — and
  // the same org across environments — stay logged in side by side (select later
  // with `--org`). Fall back to the historical env / "default" key when the
  // server didn't report an org id.
  const primaryKey = finalOrgId
    ? orgProfileKey(finalOrgId, envLabel)
    : (env ? envToProfile(env) : "default");

  const filePath = await saveAuth(sessionAuth, primaryKey);
  // Mirror into the env-name key ONLY for `--env <env>` flows, so that literal
  // `--env <env>` resolution keeps working. We deliberately do NOT mirror into
  // the bare "default" key: nothing reads that literal key (bare commands resolve
  // via `auth.default`, set below), and mirroring there would clobber a different
  // org's previously-stored "default" session.
  if (env) {
    const envKey = envToProfile(env);
    if (envKey !== primaryKey) await saveAuth(sessionAuth, envKey);
  }
  // Make the freshly logged-in org the active default for bare (no-flag)
  // commands when the user logged in without pinning a specific `--env`, or when
  // they explicitly picked an org — mirroring the prior single-session behavior.
  // A plain `login --env <env>` leaves the existing default untouched.
  if (!env || orgFlag) {
    await setDefaultProfile(primaryKey);
  }

  const displayName = finalOrgName ? ` to ${finalOrgName}` : "";
  const envSuffix = envLabel ? ` (${envLabel})` : "";
  console.log(`Login successful${displayName}${envSuffix}. Token saved to ${filePath}`);
  if (finalOrgName) {
    console.log(`Select this org later with: canary <command> --org "${finalOrgName}"`);
  }
  console.log("Set CANARY_API_TOKEN to use the CLI without re-login.");
}
